import { type AnyAction, combineReducers } from 'redux';
import cloneDeep from 'lodash/cloneDeep';

import type {
    AssetContainerModel,
    FavoritedAsset,
    GenerativeMediaModels,
    MediaSearchRequestIconTypeEnum,
    SlideBackgroundModel,
    SortEnum,
    SuggestedMediaEntryResponse,
    TransitionedAssetError,
} from 'js/src/client';
import { AssetContainerTypes, AssetTypes, TaskResultStatusEnum } from 'js/src/client';
import type { StylePreset } from 'js/src/components/animated-explainer-videos/utils/stylePresets';
import type { UploadedFile } from 'js/src/components/sketch/FileUpload';
import { switchEditorTabAction } from 'js/src/data/app/app-actions';
import {
    addMediaToUploads,
    addNewFolder,
    addPreviousMediaGenerations,
    addRecentlyUploadedAssetToAFolder,
    changeActiveSearchContext,
    changeCorrectedMediaSearchTerm,
    changeMediaSearchTerm,
    changeRelatedMediaSearchTerms,
    changeSuggestedMediaSearchTerm,
    clearCarouselMediaResults,
    clearCompletedVideoGenerationTasks,
    clearMediaErrors,
    deleteAudioSuccess,
    deleteFolder,
    deleteMediaResult,
    editFolderName,
    moveAssetToFolder,
    receiveDamConnectionError,
    receiveMediaError,
    receiveMediaResponse,
    type ReferenceVideoPayload,
    resetFolderSearchState,
    setActiveAIMediaSubTab,
    setActiveFolder,
    setAIMediaModels,
    setAISimilarGenerations,
    setAllFavorites,
    setAudioDurationFilter,
    setCarouselMediaResults,
    setCurrentAudioPlayingStatus,
    setCurrentMediaGenerations,
    setCurrentMediaQueryScene,
    setCurrentPlayingAudioDuration,
    setCurrentPlayingAudioScrubberSeekPercent,
    setCurrentPlayingAudioSeekPercent,
    setCurrentPlayingAudioUrl,
    setFilter,
    setIsFetchingRecommendedSearchTerms,
    setIsInitializingImageGeneration,
    setIsInitializingVideoGeneration,
    setIsMediaQuerySearchLoading,
    setIsSimilarSearchContext,
    setIsSimilarSearchLoading,
    setMediaErrors,
    setMediaQuerySearchResults,
    setPreviousMediaGenerations,
    setRecommendedSearchTerms,
    setSavingAIMedia,
    setSelectedMediaLicenseFilter,
    setSelectedMediaTypeFilter,
    setSelectedReferenceImage,
    setSelectedReferenceVideo,
    setSelectedStickerType,
    setSimilarSearchOriginalAsset,
    setSimilarSearchResults,
    setSort,
    setUserSelectedGenMediaAspectRatio,
    setGenxStylePresets,
    setSelectedGenxStylePresetId,
    setVideoGenerationTask,
    showMediaQuerySuggestionError,
    startMediaSearch,
    storeAllFolders,
    storeFrequentlyUsedTags,
    toggleFavorite,
    toggleFilterByUserLevel,
    toggleMediaSourceFilter,
    updateFolderThumbnail,
    updateMediaAttribution,
    updateMediaTags,
} from 'js/src/data/media/media-actions';
import type {
    AudioDurationRange,
    CarouselMediaResults,
    EditorTabSearchContextMapping,
    MEDIA_SOURCE_OPTIONS,
    MediaFoldersTable,
    MediaResultsTable,
    MediaSourceFilter,
    ModifiedAssetGenerationResponse,
    SearchContextValueType,
    StoredAssetContainer,
    StoredFolderSearchState,
    StoredMediaFolder,
    StoredMediaQuerySample,
    StoredMediaResult,
} from 'js/src/data/media/media-types';
import {
    AI_MEDIA_SUB_TAB_OPTIONS,
    DEFAULT_SORT_METHOD,
    DEFAULT_STICKER_TYPE,
    EDITOR_TAB_TO_SEARCH_CONTEXT_MAPPING,
    FILTER_BY_LEVELS,
    isMediaSearchResponse,
    MEDIA_LICENSE_OPTIONS,
    MEDIA_STORE_PREFIX,
    MEDIA_TYPE_OPTIONS,
    SEARCH_CONTEXT,
    readonlySlideBackgroundModelIsImageBackgroundModel,
    readonlySlideBackgroundModelIsVideoBackgroundModel,
    SEARCH_CONTEXT_TO_EDITOR_TAB_MAPPING,
    slideBackgroundModelIsImageBackgroundModel,
    slideBackgroundModelIsVideoBackgroundModel,
} from 'js/src/data/media/media-types';
import {
    checkAssetUUID,
    getStoredMediaFolderOrThrow,
    resetUploadsFolderStore,
    setUploadsFolderStore,
} from 'js/src/data/media/media-utils';
import type { PlainJsStoreOrmData } from 'js/src/data/plain-js-store-orm';
import PlainJsStoreORM, { ID_FIELD, IdGenerator } from 'js/src/data/plain-js-store-orm';
import {
    assetContainerModelIsAudio,
    assetContainerModelIsBackground,
    readonlyAssetContainerModelIsBackground,
    readonlyAssetContainerModelIsIcon,
} from 'js/src/data/uploads';
import { removeUpload } from 'js/src/data/uploads/uploads-actions';
import type { DeepReadonly } from 'js/src/libs/consts';
import { withStableEmptyArray } from 'js/src/libs/consts';

type StoredFavoritedAsset = DeepReadonly<FavoritedAsset>;
type FavoriteMediaTable = PlainJsStoreOrmData<StoredFavoritedAsset, string>;
type SearchContextsByEditorTab = Readonly<EditorTabSearchContextMapping>;
type ActiveUploadsFolders = Readonly<Partial<Record<SearchContextValueType, number | null>>>;
type PlayingAudioDetails = {
    readonly url: string | null;
    readonly seekPercent: number;
    readonly scrubberSeekPercent: number;
    readonly duration: number;
    readonly isPlaying: boolean;
};

const NO_STRINGS: readonly string[] = withStableEmptyArray<string>([]);
const NO_SOURCE_FILTERS: MediaSourceFilter = {};
const INITIAL_SEARCH_CONTEXTS: SearchContextsByEditorTab = {
    ...EDITOR_TAB_TO_SEARCH_CONTEXT_MAPPING,
};
const INITIAL_AUDIO_DETAILS: PlayingAudioDetails = {
    url: null,
    seekPercent: 0,
    scrubberSeekPercent: 0,
    duration: 0,
    isPlaying: false,
};
const INITIAL_ACTIVE_UPLOADS_FOLDER: ActiveUploadsFolders = {
    [SEARCH_CONTEXT.UPLOADED_VISUALS]: null,
    [SEARCH_CONTEXT.UPLOADED_ELEMENTS]: null,
};

type MediaResultsOrm = PlainJsStoreORM<StoredMediaResult, string>;
type MediaFoldersOrm = PlainJsStoreORM<StoredMediaFolder, number>;
type KeyPath = readonly (string | number)[];

const NO_MEDIA_RESULTS: MediaResultsTable = PlainJsStoreORM.createEmpty<StoredMediaResult, string>()
    .data;
const NO_MEDIA_FOLDERS: MediaFoldersTable = PlainJsStoreORM.createEmpty<StoredMediaFolder, number>()
    .data;
const NO_CAROUSEL_RESULTS: CarouselMediaResults = {};
const NO_ASSET_CONTAINERS: readonly StoredAssetContainer[] =
    withStableEmptyArray<StoredAssetContainer>([]);
const NO_MEDIA_QUERY_SAMPLES: readonly StoredMediaQuerySample[] =
    withStableEmptyArray<StoredMediaQuerySample>([]);

const toStoredMediaResult = (
    asset: StoredAssetContainer,
    extraFields: { readonly current_session_upload?: boolean } = {},
): StoredMediaResult => ({
    ...cloneDeep(asset),
    ...extraFields,
    [ID_FIELD]: IdGenerator.generateId(MEDIA_STORE_PREFIX),
});

const pushMediaResult = (
    orm: MediaResultsOrm,
    asset: StoredAssetContainer,
    extraFields?: { readonly current_session_upload?: boolean },
) => orm.pushItem(toStoredMediaResult(asset, extraFields), ID_FIELD);

const getInPlain = (value: unknown, path: KeyPath): unknown =>
    path.reduce<unknown>(
        (current, key) =>
            typeof current === 'object' && current !== null
                ? (current as Readonly<Record<string | number, unknown>>)[key]
                : undefined,
        value,
    );

const asUpdatableValue = (value: unknown, path: readonly string[]): object => {
    if (value === undefined) {
        return {};
    }
    if (typeof value !== 'object' || value === null) {
        throw new Error(`Cannot update within non-data-structure value in path [${path.join()}]`);
    }
    return value;
};

const setInPlain = (target: object, path: readonly string[], value: unknown): object => {
    const [key, ...rest] = path;
    const record = target as Readonly<Record<string, unknown>>;
    const current = record[key];
    const next =
        rest.length === 0 ? value : setInPlain(asUpdatableValue(current, path), rest, value);
    return next === current ? target : { ...record, [key]: next };
};

const mediaFieldPath = (mediaType: AssetTypes, field: string): readonly string[] =>
    mediaType === AssetTypes.IMAGE || mediaType === AssetTypes.VIDEO
        ? [AssetContainerTypes.BACKGROUND, mediaType, field]
        : [mediaType, field];

const setMediaResultField = (
    state: MediaResultsTable,
    mediaId: string,
    path: readonly string[],
    value: unknown,
): MediaResultsTable => {
    const orm: MediaResultsOrm = new PlainJsStoreORM(state);
    if (!orm.contains(mediaId)) {
        return state;
    }
    orm.updateById(mediaId, (record) => setInPlain(record, path, value) as StoredMediaResult);
    return orm.data;
};

const mediaResultIdsMatching = (orm: MediaResultsOrm, path: KeyPath, value: unknown) =>
    orm.filter((record) => getInPlain(record, path) === value).map((record) => record._id);

const deleteMediaResultsMatching = (
    state: MediaResultsTable,
    path: KeyPath,
    value: unknown,
): MediaResultsTable => {
    const orm: MediaResultsOrm = new PlainJsStoreORM(state);
    orm.delete(mediaResultIdsMatching(orm, path, value));
    return orm.data;
};

const deleteSoleMediaResultMatching = (
    state: MediaResultsTable,
    path: KeyPath,
    value: unknown,
): MediaResultsTable => {
    const orm: MediaResultsOrm = new PlainJsStoreORM(state);
    const ids = mediaResultIdsMatching(orm, path, value);
    if (ids.length !== 1) {
        return state;
    }
    orm.delete(ids);
    return orm.data;
};

const withFolderFields = (
    folder: StoredMediaFolder,
    fields: Partial<StoredMediaFolder>,
): StoredMediaFolder =>
    (Object.keys(fields) as (keyof StoredMediaFolder)[]).every((key) => folder[key] === fields[key])
        ? folder
        : { ...folder, ...fields };

const updateStoredFolder = (
    state: MediaFoldersTable,
    folderId: number,
    updater: (folder: StoredMediaFolder) => StoredMediaFolder,
): MediaFoldersTable => {
    const orm: MediaFoldersOrm = new PlainJsStoreORM(state);
    if (!orm.contains(folderId)) {
        return state;
    }
    orm.updateById(folderId, updater);
    return orm.data;
};

const updateMediaResults = (
    mediaResults: MediaResultsTable,
    update: (orm: MediaResultsOrm) => void,
): MediaResultsTable => {
    const orm: MediaResultsOrm = new PlainJsStoreORM(mediaResults);
    update(orm);
    return orm.data;
};

/*
    Data stored for each search context
*/

export function media_results(
    state: MediaResultsTable = NO_MEDIA_RESULTS,
    action: AnyAction,
): MediaResultsTable {
    if (startMediaSearch.match(action)) {
        return action.payload.append_results ? state : NO_MEDIA_RESULTS;
    }
    if (receiveMediaResponse.match(action)) {
        const orm: MediaResultsOrm = new PlainJsStoreORM(
            action.payload.append_results ? state : NO_MEDIA_RESULTS,
        );

        // push any premium results into the orm
        const new_premium_results = isMediaSearchResponse(action.payload.response)
            ? action.payload.response.premium_results
            : [];
        const premium_results_to_interleave: Record<number, AssetContainerModel> = {}; // dict of form {index_to_append: value}
        if (new_premium_results) {
            /*
            There are premium assets which we want to INTERLEAVE with the other results.  We want it to be
            deterministic but also somewhat random.  How can we do this? By using the result's UUIDs!
            What we'll do is divide the number of results into buckets, one for each premium result.  Then
            we'll determine where to add the premium result based on it's UUID (specifically the first char).
             */
            // for now, cap the number of premium results per page to 2 - once finalized, remove this
            const num_premium_results = Math.min(new_premium_results.length, 2);

            // determine the buckets
            const bucket_size = Math.floor(
                action.payload.response.results.length / num_premium_results,
            );

            for (let i = 0; i < num_premium_results; i++) {
                // determine the relative position in the bucket based on the hex value of the first char of uuid
                const result = new_premium_results[i];
                if (assetContainerModelIsAudio(result)) {
                    throw Error(
                        'There should not be premium results for audio assets - unsupported currently!',
                    );
                }
                if (!assetContainerModelIsBackground(result)) {
                    throw Error('only background assets are supported for premium results');
                }
                const background = result[result['type']];
                const asset = background[background['type']];
                if (!asset) {
                    throw Error('asset not found in premium result');
                }

                const uuid_first_char = checkAssetUUID(asset['uuid'])[0];
                const uuid_to_number = parseInt(`0x${uuid_first_char}`);
                const offset = Math.floor((uuid_to_number * bucket_size) / 15);
                premium_results_to_interleave[i * bucket_size + offset] = result;
            }
        }

        // push all of the results into the orm
        for (let i = 0; i < action.payload.response.results.length; i++) {
            // if there's a premium result to interleave, interleave it! :D
            if (premium_results_to_interleave[i]) {
                pushMediaResult(orm, premium_results_to_interleave[i]);
            }

            pushMediaResult(orm, action.payload.response.results[i]);
        }

        // handle case where no normal results but there is a premium result
        if (action.payload.response.results.length === 0) {
            // if there's a premium result to interleave, interleave it! :D
            if (premium_results_to_interleave[0]) {
                pushMediaResult(orm, premium_results_to_interleave[0]);
            }
        }

        return orm.data;
    }
    if (moveAssetToFolder.match(action) || removeUpload.match(action)) {
        const { asset } = action.payload;
        if (readonlyAssetContainerModelIsBackground(asset)) {
            const assetType = AssetContainerTypes.BACKGROUND;
            const background = asset.background;
            const slideBackgroundType = background.type;
            let uuid;
            if (readonlySlideBackgroundModelIsImageBackgroundModel(background)) {
                uuid = background.image.uuid;
            } else if (readonlySlideBackgroundModelIsVideoBackgroundModel(background)) {
                uuid = background.video.uuid;
            } else {
                throw new Error('unknown slide background type');
            }
            return deleteSoleMediaResultMatching(
                state,
                [assetType, slideBackgroundType, 'uuid'],
                uuid,
            );
        } else if (readonlyAssetContainerModelIsIcon(asset)) {
            return deleteSoleMediaResultMatching(
                state,
                [AssetContainerTypes.ICON, 'uuid'],
                asset.icon.uuid,
            );
        }
        return state;
    }
    if (deleteMediaResult.match(action)) {
        const { mediaUuid, assetType, slideBackgroundType } = action.payload;
        return deleteMediaResultsMatching(
            state,
            [assetType, slideBackgroundType, 'uuid'],
            mediaUuid,
        );
    }
    if (updateMediaTags.match(action)) {
        return setMediaResultField(
            state,
            action.payload.mediaORMId,
            mediaFieldPath(action.payload.mediaType, 'tags'),
            action.payload.mediaTags,
        );
    }
    if (updateMediaAttribution.match(action)) {
        return setMediaResultField(
            state,
            action.payload.mediaORMId,
            mediaFieldPath(action.payload.mediaType, 'attribution'),
            action.payload.mediaAttribution,
        );
    }
    if (addMediaToUploads.match(action)) {
        // when we have a new upload, store this in media_results as well, but tag it as current_session_upload
        // so we can prioritize it in our UI
        const asset = action.payload.mediaAsset;
        if (!asset) {
            return state;
        }
        const orm: MediaResultsOrm = new PlainJsStoreORM(state);
        orm.pushItemToFront(toStoredMediaResult(asset, { current_session_upload: true }), ID_FIELD);
        return orm.data;
    }
    if (deleteAudioSuccess.match(action)) {
        return deleteMediaResultsMatching(state, ['audio', 'id'], action.payload.id);
    }
    return state;
}

export function no_results(state = false, action: AnyAction) {
    if (startMediaSearch.match(action)) {
        if (!action.payload.append_results) {
            // if we are starting a new search, we shouldn't
            // assume there are no results.
            return false;
        }
        return state;
    }
    if (receiveMediaResponse.match(action)) {
        return (
            action.payload.response.results.length === 0 &&
            action.payload.num_existing_results === 0
        );
    }
    return state;
}

export function no_access(state = false, action: AnyAction) {
    if (receiveMediaResponse.match(action)) {
        return action.payload.response.error === 'no_access';
    }
    if (receiveDamConnectionError.match(action)) {
        return true;
    }
    return state;
}

export function more_results(state = true, action: AnyAction) {
    if (receiveMediaResponse.match(action)) {
        return action.payload.response.next !== null;
    }
    if (receiveMediaError.match(action)) {
        return false;
    }
    return state;
}

export function page(state = 1, action: AnyAction) {
    if (startMediaSearch.match(action)) {
        if (action.payload.append_results === false) {
            return 1;
        }
        return state;
    }
    if (receiveMediaResponse.match(action)) {
        return action.payload.page + 1;
    }
    return state;
}

/**
 * Only used for TENOR search
 */
export function pos(state: null | string = null, action: AnyAction) {
    if (startMediaSearch.match(action)) {
        if (action.payload.append_results === false) {
            return null;
        }
        return state;
    }
    if (receiveMediaResponse.match(action)) {
        return action.payload.pos;
    }
    return state;
}

/**
 * Used for DAM search
 */
export function next(state: null | string = null, action: AnyAction) {
    if (startMediaSearch.match(action)) {
        if (action.payload.append_results === false) {
            return null;
        }
        return state;
    }
    if (receiveMediaResponse.match(action)) {
        return action.payload.response.next;
    }
    return state;
}

export function last_searched_term(state = '', action: AnyAction) {
    if (startMediaSearch.match(action)) {
        return action.payload.search_term;
    }
    return state;
}

export function last_used_filters(state: string[] = [], action: AnyAction) {
    if (startMediaSearch.match(action)) {
        return action.payload.filter;
    }
    return state;
}

export function search_term(state = '', action: AnyAction) {
    if (changeMediaSearchTerm.match(action)) {
        return action.payload.search_term;
    }
    return state;
}

export function corrected_search_term(state = '', action: AnyAction) {
    if (changeCorrectedMediaSearchTerm.match(action)) {
        return action.payload.search_term;
    }
    if (startMediaSearch.match(action)) {
        return action.payload.page === 1 ? '' : state;
    }
    return state;
}

export function corrected_search_term_indices(
    state: readonly number[] | null = null,
    action: AnyAction,
): readonly number[] | null {
    if (changeCorrectedMediaSearchTerm.match(action)) {
        return withStableEmptyArray([...action.payload.search_term_indices]);
    }
    return state;
}

export function suggested_search_term(state = '', action: AnyAction) {
    if (startMediaSearch.match(action)) {
        return action.payload.page === 1 ? '' : state;
    }
    if (changeSuggestedMediaSearchTerm.match(action)) {
        return action.payload.query;
    }
    return state;
}

export function highlighted_suggested_search_term(state = '', action: AnyAction) {
    if (changeSuggestedMediaSearchTerm.match(action)) {
        return action.payload.highlighted_query;
    }
    return state;
}

export function related_search_terms(
    state: readonly string[] = NO_STRINGS,
    action: AnyAction,
): readonly string[] {
    if (changeRelatedMediaSearchTerms.match(action)) {
        return withStableEmptyArray([...action.payload.popular_queries]);
    }
    return state;
}

export function frequently_used_tags(
    state: readonly string[] = NO_STRINGS,
    action: AnyAction,
): readonly string[] {
    if (storeFrequentlyUsedTags.match(action)) {
        return withStableEmptyArray(action.payload.frequently_used_tags);
    }
    return state;
}

export function sourceFilter(
    state: MediaSourceFilter = NO_SOURCE_FILTERS,
    action: AnyAction,
): MediaSourceFilter {
    /*
    The filter is a map of the different ways that the media results
    can be filtered. In order for a type of media to be filtered out,
    the property must exist in the state AND be set to false. For example,
    this would filter out premium media, but leave basic media:
    {
        premium: false
        // no 'basic' property, so defaults to "true"
    }

    This is used to power the filter for media search, specifically the "source" checkboxes:
    - [ ] Basic
    - [ ] Premium
    - [ ] Marketplace
    */
    if (setFilter.match(action)) {
        return action.payload.filter;
    }
    if (toggleMediaSourceFilter.match(action)) {
        const { filter_key, filter_value } = action.payload;
        return state[filter_key as MEDIA_SOURCE_OPTIONS] === filter_value
            ? state
            : { ...state, [filter_key]: filter_value };
    }
    return state;
}

export function selectedType(state = MEDIA_TYPE_OPTIONS.ALL, action: AnyAction) {
    /*
    This is used to power the filter for media search, specifically the "type" radios:
    - ( ) All
    - ( ) Videos
    - ( ) Images
     */
    if (setSelectedMediaTypeFilter.match(action)) {
        return action.payload.selected_type;
    }
    return state;
}

export function audioDurationFilter(
    state: AudioDurationRange = { min: 0, max: null },
    action: AnyAction,
): AudioDurationRange {
    /*
    This is used to power the duration filter for audio search:
    - min: minimum duration in seconds
    - max: maximum duration in seconds (null for no max)
     */
    if (setAudioDurationFilter.match(action)) {
        return action.payload.duration_range;
    }
    return state;
}

export function selectedLicense(state = MEDIA_LICENSE_OPTIONS.ALL, action: AnyAction) {
    /*
    This is used to power the filter for media search, specifically the "license type" radios:
    - ( ) All
    - ( ) Stock
    - ( ) Editorial
     */
    if (setSelectedMediaLicenseFilter.match(action)) {
        return action.payload.selected_license_type;
    }
    return state;
}

export function filter_by_user_level(
    state = FILTER_BY_LEVELS.filter_by_workspace_level,
    action: AnyAction,
) {
    if (toggleFilterByUserLevel.match(action)) {
        return action.payload.filter_by_user_level;
    }
    return state;
}

export function selectedStickerType(
    state: MediaSearchRequestIconTypeEnum = DEFAULT_STICKER_TYPE,
    action: AnyAction,
): MediaSearchRequestIconTypeEnum {
    if (setSelectedStickerType.match(action)) {
        return action.payload.selected_sticker_type;
    }
    return state;
}

export function sort(state: SortEnum = DEFAULT_SORT_METHOD, action: AnyAction) {
    if (setSort.match(action)) {
        return action.payload.sort_method;
    }
    return state;
}

export function isSearchInProgress(state = false, action: AnyAction) {
    if (setActiveFolder.match(action)) {
        if (!action.payload.folderId) {
            return false;
        }
        return state;
    }
    if (startMediaSearch.match(action)) {
        return true;
    }
    if (receiveMediaResponse.match(action) || receiveMediaError.match(action)) {
        if (!action.payload.folderId) {
            return false;
        }
        return state;
    }
    return state;
}
/*
    End data stored for each search context
*/
export function search_context(
    state: SearchContextsByEditorTab = INITIAL_SEARCH_CONTEXTS,
    action: AnyAction,
): SearchContextsByEditorTab {
    if (changeActiveSearchContext.match(action)) {
        const searchContext = action.payload.search_context;
        const editorTab = SEARCH_CONTEXT_TO_EDITOR_TAB_MAPPING[searchContext];
        return state[editorTab] === searchContext
            ? state
            : { ...state, [editorTab]: searchContext };
    }
    return state;
}

export function current_media_query_scene(state: string | null = null, action: AnyAction) {
    if (setCurrentMediaQueryScene.match(action)) {
        return action.payload.sceneId;
    }
    return state;
}

export function is_media_query_search_loading(state = false, action: AnyAction) {
    if (setIsMediaQuerySearchLoading.match(action)) {
        return action.payload.isLoading;
    }
    return state;
}

export function media_query_search_results(
    state: readonly StoredMediaQuerySample[] = NO_MEDIA_QUERY_SAMPLES,
    action: AnyAction,
): readonly StoredMediaQuerySample[] {
    if (setMediaQuerySearchResults.match(action)) {
        // The backend returns SlideBackgroundModels, but we want to store them as AssetContainerModels
        // so we can have compatibility with the other media tabs.
        // In the future, we can refactor the backend to return AssetContainerModels directly.
        const querySamples = action.payload.searchResults.map(
            (querySample: SuggestedMediaEntryResponse) => {
                const assetContainers: AssetContainerModel[] = querySample.results.map(
                    (background: SlideBackgroundModel) => {
                        return {
                            type: AssetContainerTypes.BACKGROUND,
                            background,
                            audio: null,
                            icon: null,
                            font: null,
                            document: null,
                            folder_id: null,
                            article_id: null,
                            document_id: null,
                            visual_description: null,
                        };
                    },
                );
                return {
                    ...querySample,
                    results: assetContainers,
                };
            },
        );
        return withStableEmptyArray(cloneDeep(querySamples));
    }
    return state;
}

export function show_media_query_suggestion_error(state = false, action: AnyAction) {
    if (showMediaQuerySuggestionError.match(action)) {
        return action.payload.show;
    }
    return state;
}

export function is_similar_search_context(state = false, action: AnyAction): boolean {
    if (setIsSimilarSearchContext.match(action)) {
        return action.payload;
    }
    return state;
}

export function is_similar_search_loading(state = false, action: AnyAction): boolean {
    if (setIsSimilarSearchLoading.match(action)) {
        return action.payload;
    }
    return state;
}

export function similar_search_results(
    state: readonly StoredAssetContainer[] = NO_ASSET_CONTAINERS,
    action: AnyAction,
): readonly StoredAssetContainer[] {
    if (setSimilarSearchResults.match(action)) {
        return withStableEmptyArray<StoredAssetContainer>(action.payload.results);
    }
    return state;
}

export function similar_search_original_asset(
    state: StoredAssetContainer | null = null,
    action: AnyAction,
): StoredAssetContainer | null {
    if (setSimilarSearchOriginalAsset.match(action)) {
        return action.payload.originalAsset;
    }
    return state;
}

export function ai_similar_generations(
    state: ModifiedAssetGenerationResponse[] = [],
    action: AnyAction,
): ModifiedAssetGenerationResponse[] {
    if (setAISimilarGenerations.match(action)) {
        return action.payload.append
            ? [...state, ...action.payload.results]
            : action.payload.results;
    }
    return state;
}

export function selected_reference_image(
    state: UploadedFile | null = null,
    action: AnyAction,
): UploadedFile | null {
    if (setSelectedReferenceImage.match(action)) {
        return action.payload;
    }
    return state;
}

export function selected_reference_video(
    state: ReferenceVideoPayload | null = null,
    action: AnyAction,
): ReferenceVideoPayload | null {
    if (setSelectedReferenceVideo.match(action)) {
        return action.payload;
    }
    return state;
}

export function active_ai_media_sub_tab(state = AI_MEDIA_SUB_TAB_OPTIONS.IMAGE, action: AnyAction) {
    if (setActiveAIMediaSubTab.match(action)) {
        return action.payload;
    }
    return state;
}

function updateAIMediaGenerationSavingState(
    state: Array<ModifiedAssetGenerationResponse>,
    payload: { generationUuid: string; isSaving: boolean },
): Array<ModifiedAssetGenerationResponse> {
    const { generationUuid, isSaving } = payload;
    return state.map((generation) => {
        if (generation?.result?.uuid === generationUuid) {
            return {
                ...generation,
                result: {
                    ...generation.result,
                    isSaving,
                },
            };
        }
        return generation;
    });
}

export function current_media_generations(
    state: Array<ModifiedAssetGenerationResponse> = [],
    action: AnyAction,
): Array<ModifiedAssetGenerationResponse> {
    if (setCurrentMediaGenerations.match(action)) {
        return action.payload;
    }
    if (setSavingAIMedia.match(action)) {
        const s = updateAIMediaGenerationSavingState(state, action.payload);
        return s;
    }
    return state;
}

export function previous_media_generations(
    state: Array<ModifiedAssetGenerationResponse> = [],
    action: AnyAction,
): Array<ModifiedAssetGenerationResponse> {
    if (setPreviousMediaGenerations.match(action)) {
        return action.payload;
    }
    if (addPreviousMediaGenerations.match(action)) {
        // Reverse the previous media generations so that the most recent generations are shown first
        const reversedMediaGenerations = [...action.payload].reverse();
        return [...reversedMediaGenerations, ...state];
    }
    if (setSavingAIMedia.match(action)) {
        return updateAIMediaGenerationSavingState(state, action.payload);
    }
    return state;
}

export function is_fetching_recommended_search_terms(state = false, action: AnyAction) {
    if (setIsFetchingRecommendedSearchTerms.match(action)) {
        return action.payload;
    }
    return state;
}

export function recommended_search_terms(
    state: readonly string[] = NO_STRINGS,
    action: AnyAction,
): readonly string[] {
    if (setRecommendedSearchTerms.match(action)) {
        return withStableEmptyArray(action.payload.terms);
    }
    return state;
}

export function all_favorite_media(
    state: FavoriteMediaTable = PlainJsStoreORM.createEmpty<StoredFavoritedAsset, string>().data,
    action: AnyAction,
): FavoriteMediaTable {
    if (setAllFavorites.match(action)) {
        const orm = new PlainJsStoreORM(state);
        action.payload.allFavoriteMedia.forEach((favoritedAsset) => {
            orm.pushItem(favoritedAsset, 'uuid');
        });
        return orm.data;
    }
    if (toggleFavorite.match(action)) {
        const { favorite, ...favoritedAsset } = action.payload;
        const orm = new PlainJsStoreORM(state);
        if (favorite) {
            orm.pushItem(favoritedAsset, 'uuid');
        } else {
            orm.delete([favoritedAsset.uuid]);
        }
        return orm.data;
    }
    return state;
}

const withAudioDetail = <Key extends keyof PlayingAudioDetails>(
    state: PlayingAudioDetails,
    key: Key,
    value: PlayingAudioDetails[Key],
): PlayingAudioDetails => (state[key] === value ? state : { ...state, [key]: value });

export function playingAudioDetails(
    state: PlayingAudioDetails = INITIAL_AUDIO_DETAILS,
    action: AnyAction,
): PlayingAudioDetails {
    if (switchEditorTabAction.match(action)) {
        // stop any playing audio when switching editor tabs
        return withAudioDetail(state, 'isPlaying', false);
    }
    if (setCurrentPlayingAudioUrl.match(action)) {
        return withAudioDetail(state, 'url', action.payload.audioUrl);
    }
    if (setCurrentPlayingAudioSeekPercent.match(action)) {
        return withAudioDetail(state, 'seekPercent', action.payload.seekPercent);
    }
    if (setCurrentPlayingAudioScrubberSeekPercent.match(action)) {
        return withAudioDetail(state, 'scrubberSeekPercent', action.payload.scrubberSeekPercent);
    }
    if (setCurrentPlayingAudioDuration.match(action)) {
        return withAudioDetail(state, 'duration', action.payload.duration);
    }
    if (setCurrentAudioPlayingStatus.match(action)) {
        return withAudioDetail(state, 'isPlaying', action.payload.isPlaying);
    }
    return state;
}

export function active_uploads_folder(
    state: ActiveUploadsFolders = INITIAL_ACTIVE_UPLOADS_FOLDER,
    action: AnyAction,
): ActiveUploadsFolders {
    if (setActiveFolder.match(action)) {
        if (
            action.payload.searchContext !== SEARCH_CONTEXT.UPLOADED_VISUALS &&
            action.payload.searchContext !== SEARCH_CONTEXT.UPLOADED_ELEMENTS
        ) {
            throw Error(
                `You cannot set the active folder for a search context that does not support folders. Got ${action.payload.searchContext}`,
            );
        }
        const { searchContext, folderId } = action.payload;
        return state[searchContext] === folderId ? state : { ...state, [searchContext]: folderId };
    }
    return state;
}

export function all_uploads_folders(
    state: MediaFoldersTable = NO_MEDIA_FOLDERS,
    action: AnyAction,
): MediaFoldersTable {
    if (storeAllFolders.match(action)) {
        const orm: MediaFoldersOrm = new PlainJsStoreORM(state);
        action.payload.folders.forEach((folder) => {
            orm.pushItem(setUploadsFolderStore(folder), 'id');
        });
        return orm.data;
    }
    if (addNewFolder.match(action)) {
        const orm: MediaFoldersOrm = new PlainJsStoreORM(state);
        orm.pushItemToFront(setUploadsFolderStore(action.payload.folder), 'id');
        return orm.data;
    }
    if (editFolderName.match(action)) {
        const folderFields = action.payload.folder;
        getStoredMediaFolderOrThrow(state, folderFields.id);
        return updateStoredFolder(state, folderFields.id, (folder) =>
            withFolderFields(folder, folderFields),
        );
    }
    if (deleteFolder.match(action)) {
        const orm: MediaFoldersOrm = new PlainJsStoreORM(state);
        orm.delete([action.payload.folderId]);
        return orm.data;
    }
    if (addRecentlyUploadedAssetToAFolder.match(action)) {
        const { folderId, mediaAsset } = action.payload;
        const folderDetails = getStoredMediaFolderOrThrow(state, folderId);
        let thumbnailUrl = '';
        if (assetContainerModelIsBackground(mediaAsset)) {
            // we can only have preview for a image or video. Icons will use default folder preview
            const background = mediaAsset.background;
            if (slideBackgroundModelIsImageBackgroundModel(background)) {
                thumbnailUrl = background.image.thumbnail_url;
            } else if (slideBackgroundModelIsVideoBackgroundModel(background)) {
                thumbnailUrl = background.video.poster_url;
            }
        }
        const mediaResults = updateMediaResults(folderDetails.media_results, (orm) =>
            pushMediaResult(orm, mediaAsset, { current_session_upload: true }),
        );
        return updateStoredFolder(state, folderId, (folder) =>
            withFolderFields(folder, {
                media_results: mediaResults,
                media_count: folderDetails.media_count + 1,
                thumbnail_url: thumbnailUrl,
            }),
        );
    }
    if (removeUpload.match(action)) {
        const { activeFolderId, asset } = action.payload;
        if (!activeFolderId) {
            return state;
        }
        const assetIds = ID_FIELD in asset ? [asset[ID_FIELD]] : [];
        const folderDetails = getStoredMediaFolderOrThrow(state, activeFolderId);
        const mediaResults = updateMediaResults(folderDetails.media_results, (orm) =>
            orm.delete(assetIds),
        );

        // if we happen to delete the asset that is thumbnail, set the icon to null
        // todo: could change this to call an endpoint to refresh the folder, but for now too expensive
        let thumbnailUrl = '';
        if (readonlyAssetContainerModelIsBackground(asset)) {
            const background = asset.background;
            if (readonlySlideBackgroundModelIsImageBackgroundModel(background)) {
                thumbnailUrl = background.image.thumbnail_url;
            } else if (readonlySlideBackgroundModelIsVideoBackgroundModel(background)) {
                thumbnailUrl = background.video.poster_url;
            }
        }
        return updateStoredFolder(state, activeFolderId, (folder) => {
            const updated = withFolderFields(folder, {
                media_results: mediaResults,
                media_count: folderDetails.media_count - 1,
            });
            return thumbnailUrl === updated.thumbnail_url
                ? withFolderFields(updated, { thumbnail_url: undefined })
                : updated;
        });
    }
    if (moveAssetToFolder.match(action)) {
        const { asset, sourceFolderId, targetFolderId } = action.payload;
        const assetIds = ID_FIELD in asset ? [asset[ID_FIELD]] : [];
        let nextState = state;
        // If we're moving from inside a folder to another folder, remove it from there.
        if (sourceFolderId && sourceFolderId !== targetFolderId) {
            const sourceFolder = getStoredMediaFolderOrThrow(nextState, sourceFolderId);
            const sourceMediaResults = updateMediaResults(sourceFolder.media_results, (orm) =>
                orm.delete(assetIds),
            );
            nextState = updateStoredFolder(nextState, sourceFolderId, (folder) =>
                withFolderFields(folder, {
                    media_results: sourceMediaResults,
                    media_count: sourceFolder.media_count - 1,
                }),
            );
        }
        const targetFolder = getStoredMediaFolderOrThrow(nextState, targetFolderId);
        const targetMediaResults = updateMediaResults(targetFolder.media_results, (orm) =>
            pushMediaResult(orm, asset),
        );
        return updateStoredFolder(nextState, targetFolderId, (folder) =>
            withFolderFields(folder, {
                media_results: targetMediaResults,
                media_count: targetFolder.media_count + 1,
            }),
        );
    }
    if (updateMediaTags.match(action) || updateMediaAttribution.match(action)) {
        const folderId = action.payload.folderId;
        if (!folderId) {
            return state;
        }
        const folderDetails = getStoredMediaFolderOrThrow(state, folderId);
        const mediaResults = updateMediaTags.match(action)
            ? setMediaResultField(
                  folderDetails.media_results,
                  action.payload.mediaORMId,
                  mediaFieldPath(action.payload.mediaType, 'tags'),
                  action.payload.mediaTags,
              )
            : setMediaResultField(
                  folderDetails.media_results,
                  action.payload.mediaORMId,
                  mediaFieldPath(action.payload.mediaType, 'attribution'),
                  action.payload.mediaAttribution,
              );
        return updateStoredFolder(state, folderId, (folder) =>
            withFolderFields(folder, { media_results: mediaResults }),
        );
    }
    if (setSort.match(action)) {
        const folderId = action.payload.folderId;
        if (!folderId) {
            return state;
        }
        getStoredMediaFolderOrThrow(state, folderId);
        return updateStoredFolder(state, folderId, (folder) =>
            withFolderFields(resetUploadsFolderStore(folder), { sort: action.payload.sort_method }),
        );
    }
    if (toggleMediaSourceFilter.match(action)) {
        const folderId = action.payload.folderId;
        if (!folderId) {
            return state;
        }
        const { filter_key, filter_value } = action.payload;
        return updateStoredFolder(state, folderId, (folder) => {
            const sourceFilter =
                folder.sourceFilter[filter_key as MEDIA_SOURCE_OPTIONS] === filter_value
                    ? folder.sourceFilter
                    : { ...folder.sourceFilter, [filter_key]: filter_value };
            return withFolderFields(resetUploadsFolderStore(folder), { sourceFilter });
        });
    }
    if (setSelectedMediaTypeFilter.match(action)) {
        const folderId = action.payload.folderId;
        if (!folderId) {
            return state;
        }
        getStoredMediaFolderOrThrow(state, folderId);
        return updateStoredFolder(state, folderId, (folder) =>
            withFolderFields(resetUploadsFolderStore(folder), {
                selectedType: action.payload.selected_type,
            }),
        );
    }
    if (resetFolderSearchState.match(action)) {
        return updateStoredFolder(state, action.payload.folder_id, resetUploadsFolderStore);
    }
    if (startMediaSearch.match(action)) {
        const folderId = action.payload.folderId;
        if (!folderId) {
            return state;
        }
        return updateStoredFolder(state, folderId, (folder) =>
            withFolderFields(folder, { isSearchInProgress: true }),
        );
    }
    if (receiveMediaResponse.match(action)) {
        const folderId = action.payload.folderId;
        if (!folderId) {
            return state;
        }
        const folderDetails = getStoredMediaFolderOrThrow(state, folderId);
        const searchState = folderSearchReducer(pickFolderSearchState(folderDetails), action);
        return updateStoredFolder(state, folderId, (folder) =>
            withFolderFields(folder, { ...searchState, isSearchInProgress: false }),
        );
    }
    if (changeMediaSearchTerm.match(action)) {
        const folderId = action.payload.folderId;
        if (!folderId) {
            return state;
        }
        const folderDetails = getStoredMediaFolderOrThrow(state, folderId);
        const reducedSearchTerm = search_term(folderDetails.search_term, action);
        return updateStoredFolder(state, folderId, (folder) =>
            withFolderFields(folder, { search_term: reducedSearchTerm }),
        );
    }
    if (updateFolderThumbnail.match(action)) {
        const { srcFolderId, srcThumbnailUrl, targetFolderId, targetThumbnailUrl } = action.payload;
        const withSourceThumbnail = srcFolderId
            ? updateStoredFolder(state, srcFolderId, (folder) =>
                  withFolderFields(folder, { thumbnail_url: srcThumbnailUrl }),
              )
            : state;
        return targetFolderId
            ? updateStoredFolder(withSourceThumbnail, targetFolderId, (folder) =>
                  withFolderFields(folder, { thumbnail_url: targetThumbnailUrl }),
              )
            : withSourceThumbnail;
    }
    if (storeFrequentlyUsedTags.match(action)) {
        const folderId = action.payload.folderId;
        if (!folderId) {
            return state;
        }
        return updateStoredFolder(state, folderId, (folder) =>
            withFolderFields(folder, {
                frequently_used_tags: frequently_used_tags(folder.frequently_used_tags, action),
            }),
        );
    }
    return state;
}

export function media_errors(
    state: TransitionedAssetError[] | null = null,
    action: AnyAction,
): TransitionedAssetError[] | null {
    if (setMediaErrors.match(action)) {
        return action.payload.errors;
    }
    if (clearMediaErrors.match(action)) {
        return null;
    }
    return state;
}

export function ai_media_models(
    state: GenerativeMediaModels[] = [],
    action: AnyAction,
): GenerativeMediaModels[] {
    if (setAIMediaModels.match(action)) {
        return action.payload;
    }
    return state;
}

export function video_generation_tasks(
    state: Record<string, { status: TaskResultStatusEnum; result?: SlideBackgroundModel }> = {},
    action: AnyAction,
): Record<string, { status: TaskResultStatusEnum; result?: SlideBackgroundModel }> {
    if (setVideoGenerationTask.match(action)) {
        const { taskId, ...taskData } = action.payload;
        return {
            ...state,
            [taskId]: taskData,
        };
    }
    if (clearCompletedVideoGenerationTasks.match(action)) {
        const filtered: typeof state = {};
        for (const [taskId, task] of Object.entries(state)) {
            if (task.status === TaskResultStatusEnum.PENDING) {
                filtered[taskId] = task;
            }
        }
        return filtered;
    }
    return state;
}

export function is_initializing_video_generation(state = false, action: AnyAction) {
    if (setIsInitializingVideoGeneration.match(action)) {
        return action.payload;
    }
    return state;
}

export function is_initializing_image_generation(state = false, action: AnyAction) {
    if (setIsInitializingImageGeneration.match(action)) {
        return action.payload;
    }
    return state;
}

export function carousel_media_results(
    state: CarouselMediaResults = NO_CAROUSEL_RESULTS,
    action: AnyAction,
): CarouselMediaResults {
    if (setCarouselMediaResults.match(action)) {
        const { categoryId, results } = action.payload;
        const categoryResults = updateMediaResults(NO_MEDIA_RESULTS, (orm) =>
            results.forEach((result) => pushMediaResult(orm, result)),
        );
        return {
            ...state,
            [categoryId]: categoryResults,
        };
    }
    if (clearCarouselMediaResults.match(action)) {
        return NO_CAROUSEL_RESULTS;
    }
    return state;
}

export function user_selected_gen_media_aspect_ratio(
    state: string | null = null,
    action: AnyAction,
): string | null {
    if (setUserSelectedGenMediaAspectRatio.match(action)) {
        return action.payload;
    }
    return state;
}

export function genx_style_presets(state: StylePreset[] = [], action: AnyAction): StylePreset[] {
    if (setGenxStylePresets.match(action)) {
        return action.payload;
    }
    return state;
}

export function selected_genx_style_preset_id(
    state: string | null = null,
    action: AnyAction,
): string | null {
    if (setSelectedGenxStylePresetId.match(action)) {
        return action.payload;
    }
    return state;
}

export const media_reducers_map = {
    media_results,
    no_access,
    no_results,
    more_results,
    page,
    pos,
    next,
    last_searched_term,
    last_used_filters,
    search_term,
    isSearchInProgress,
    sourceFilter,
    selectedType,
    audioDurationFilter,
    selectedLicense,
    selectedStickerType,
    filter_by_user_level,
    sort,
    corrected_search_term,
    corrected_search_term_indices,
    suggested_search_term,
    highlighted_suggested_search_term,
    related_search_terms,
    frequently_used_tags,
    carousel_media_results,
};

const folderSearchReducer = combineReducers(media_reducers_map);

const pickFolderSearchState = (folder: StoredMediaFolder): StoredFolderSearchState =>
    Object.fromEntries(
        Object.keys(media_reducers_map).map((key) => [
            key,
            folder[key as keyof StoredFolderSearchState],
        ]),
    ) as StoredFolderSearchState;

const reducer_override = (option: SearchContextValueType) => {
    const reducer = combineReducers(media_reducers_map);
    return (state: ReturnType<typeof reducer>, action: AnyAction) => {
        /*
        Override the normal reducer. Only send actions to the normal reducer
        that are either:
            - not media events
            - are media events that have a search_context property that matches
            the category that this reducer is responsible for
        */
        const sendToAllMediaReducers =
            action.sendToAllMediaReducers || action.payload?.sendToAllMediaReducers;
        if (sendToAllMediaReducers === true) {
            return reducer(state, action);
        }

        let sendToReducer = true;

        // get the search_context from the action. Could be directly attached to the action, or could
        // be inside of the action's payload.
        const searchContext: SearchContextValueType =
            action?.search_context || action?.payload?.search_context; // js-style action creators // actions using createAction
        if (searchContext !== option) {
            sendToReducer = false;
        }

        const actionHasFolderId = !!action?.folderId || !!action?.payload?.folderId;
        const actionIsInUploadedVisuals = searchContext === SEARCH_CONTEXT.UPLOADED_VISUALS;
        const actionIsInUploadedIcons = searchContext === SEARCH_CONTEXT.UPLOADED_ELEMENTS;

        if (actionHasFolderId && (actionIsInUploadedVisuals || actionIsInUploadedIcons)) {
            // folders live inside of 'uploaded-visuals' tab, so if user is inside a folder, actions would route to 2 reducers:
            // all_uploads_folders and uploaded-visuals. We want to set send_to_reducer to false if action contains folderId
            // since it'll be handled by all_uploads_folders.
            //
            // Same idea for 'uploaded-icons'.
            sendToReducer = false;
        }

        // the initialization event must be sent to the reducer so that the redux
        // state has the right shape
        if (state === undefined) {
            sendToReducer = true;
        }

        if (sendToReducer) {
            return reducer(state, action);
        } else {
            return state;
        }
    };
};

type ReducerMap = Record<SearchContextValueType, ReturnType<typeof reducer_override>>;
const reducer_map = {} as ReducerMap;

// make a reducer for each category of media that exists.
for (const option of Object.values(SEARCH_CONTEXT)) {
    reducer_map[option] = reducer_override(option);
}

export const media = combineReducers({
    search_context,
    all_favorite_media,
    playingAudioDetails,
    all_uploads_folders,
    active_uploads_folder,
    current_media_query_scene,
    is_media_query_search_loading,
    media_query_search_results,
    show_media_query_suggestion_error,
    is_similar_search_context,
    is_similar_search_loading,
    similar_search_results,
    similar_search_original_asset,
    ai_similar_generations,
    selected_reference_image,
    selected_reference_video,
    current_media_generations,
    previous_media_generations,
    is_fetching_recommended_search_terms,
    recommended_search_terms,
    media_errors,
    ai_media_models,
    video_generation_tasks,
    is_initializing_video_generation,
    is_initializing_image_generation,
    active_ai_media_sub_tab,
    user_selected_gen_media_aspect_ratio,
    genx_style_presets,
    selected_genx_style_preset_id,
    ...reducer_map,
});

// something gets messed up with the reducer_map type when we use combineReducers,
// so we add in the correct type for the reducer_map here:
export type MediaState = Omit<ReturnType<typeof media>, SearchContextValueType> &
    Record<SearchContextValueType, ReturnType<(typeof reducer_map)[SearchContextValueType]>>;

/*
 * We need to be able to undo/redo search term recommendations as users undo/redo article import
 */
export const undoable_media = combineReducers({
    is_fetching_recommended_search_terms,
    recommended_search_terms,
});
